import { useEffect, useState, useRef } from 'react';
import { encodeWAV } from '../utils/audioAnalysis';

// ============================================================
// NEUROLONIC PI RING — WEB SERIAL AUDIO HANDLER
// ============================================================
// Passively listens on USB Serial for the ESP32 Pi Ring.
//
// Flow (button-triggered by ESP32):
//   1. User presses ring button
//   2. ESP32 sends "[STREAMING]\n" then raw 8-bit PCM audio for 8 seconds
//   3. ESP32 sends "[STOPPED]\n" when done
//   4. Builds WAV → POST /predict (auto_save=true) → saved to dashboard
//   5. Returns to listening state, waits for next button press
//
// KEY DESIGN: All logic state lives in REFS. A 200ms polling interval
// syncs refs → React state. This avoids the React 18 async batching issue
// where setState() calls inside async loops are deferred until a user
// gesture (mouse move / click), causing the UI to appear frozen.
// ============================================================

const SAMPLE_RATE = 8000;
const BITS_PER_SAMPLE = 8;
const CHANNELS = 1;
const RECORD_SECONDS = 8;
const EXPECTED_BYTES = SAMPLE_RATE * RECORD_SECONDS; // 64000 bytes

// IMPORTANT: Nginx routes /app/ → predict server (Flask :5000)
//                          /api/ → backend    (FastAPI :8000)
// The predict endpoint lives at /app/predict — NOT /api/predict
const PREDICT_BASE = process.env.REACT_APP_PREDICT_URL ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:5000'
    : 'https://app.neurolonic.com/app');

const BAUD_RATE = 115200;

// Marker bytes for "[STOPPED]" (0x5B 0x53 0x54 0x4F 0x50 0x50 0x45 0x44 0x5D)
const STOPPED_MARKER = new Uint8Array([0x5B, 0x53, 0x54, 0x4F, 0x50, 0x50, 0x45, 0x44, 0x5D]);

function findMarker(data, marker) {
  for (let i = 0; i <= data.length - marker.length; i++) {
    let match = true;
    for (let j = 0; j < marker.length; j++) {
      if (data[i + j] !== marker[j]) { match = false; break; }
    }
    if (match) return i;
  }
  return -1;
}

function SerialAudioHandler({ userId, serialPort, onAudioReceived, onError, onStatusChange }) {
  // ---- UI state (synced from refs by polling interval) ----
  const [uiState, setUiState] = useState({
    status: 'idle',       // idle | listening | recording | uploading | done | error
    progress: 0,
    isRecording: false,
    autoSaved: false,
  });

  // ---- Logic state (refs — never stale, safe inside async loops) ----
  const audioBufferRef  = useRef([]);
  const totalBytesRef   = useRef(0);
  const streamingRef    = useRef(false);  // true while ESP32 is sending audio
  const uploadingRef    = useRef(false);  // true while POST /predict is in-flight
  const statusRef       = useRef('idle'); // current status string
  const autoSavedRef    = useRef(false);  // true if last upload was saved to dashboard
  const readerRef       = useRef(null);
  const writerRef       = useRef(null);
  const runningRef      = useRef(false);
  const textBufRef      = useRef('');     // incomplete text line accumulator
  const stopTimerRef    = useRef(null);   // fallback stop timer
  const micRecordingRef = useRef(false);  // true while laptop mic is recording
  const micProgressRef  = useRef(0);      // 0-100 progress for mic recording

  // Latest props — always current, never cause effect re-runs
  const userIdRef            = useRef(userId);
  const onAudioReceivedRef   = useRef(onAudioReceived);
  const onErrorRef           = useRef(onError);
  const onStatusChangeRef    = useRef(onStatusChange);
  useEffect(() => { userIdRef.current = userId; },            [userId]);
  useEffect(() => { onAudioReceivedRef.current = onAudioReceived; }, [onAudioReceived]);
  useEffect(() => { onErrorRef.current = onError; },          [onError]);
  useEffect(() => { onStatusChangeRef.current = onStatusChange; }, [onStatusChange]);

  // Manual start ref — exposed to button handler below
  const manualStartRef = useRef(null);

  // ---- Polling interval: syncs refs → React state every 200ms ----
  // This is the FIX for the React 18 async batching issue. State setters
  // called inside async serial loops are batched and held until a user
  // gesture. The interval fires on React's normal event loop instead.
  useEffect(() => {
    const interval = setInterval(() => {
      const isRingRec = streamingRef.current && !uploadingRef.current;
      const isMicRec  = micRecordingRef.current;
      setUiState({
        status: statusRef.current,
        isRecording: isRingRec || isMicRec,
        autoSaved: autoSavedRef.current,
        progress: isMicRec   ? micProgressRef.current :
                  isRingRec  ? Math.min(100, Math.round((totalBytesRef.current / EXPECTED_BYTES) * 100)) :
                  uploadingRef.current ? 100 : 0,
      });
    }, 200);
    return () => clearInterval(interval);
  }, []);

  // ---- Component-scope status setter (safe to call from any async context) ----
  function setStatus(s) {
    statusRef.current = s;
    if (onStatusChangeRef.current) onStatusChangeRef.current(s);
  }

  // ---- Laptop mic recording — called by the Manual Record button ----
  // Records 8s from the laptop microphone, builds a WAV, and POSTs it to
  // /predict exactly like the ring does. Results flow via onAudioReceived.
  async function recordFromMic() {
    if (micRecordingRef.current || uploadingRef.current || streamingRef.current) return;
    micRecordingRef.current = true;
    micProgressRef.current  = 0;
    autoSavedRef.current    = false;
    setStatus('recording');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 }
      });

      const audioCtx  = new (window.AudioContext || window.webkitAudioContext)();
      const source    = audioCtx.createMediaStreamSource(stream);
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);
      const chunks    = [];
      const sampleRate = audioCtx.sampleRate;

      source.connect(processor);
      processor.connect(audioCtx.destination);
      processor.onaudioprocess = (e) => chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));

      // Tick progress 0→99 over RECORD_SECONDS
      const startTime = Date.now();
      const ticker = setInterval(() => {
        micProgressRef.current = Math.min(99, Math.round(((Date.now() - startTime) / (RECORD_SECONDS * 1000)) * 100));
      }, 100);

      await new Promise(resolve => setTimeout(resolve, RECORD_SECONDS * 1000));

      clearInterval(ticker);
      micProgressRef.current = 100;
      stream.getTracks().forEach(t => t.stop());
      processor.disconnect();
      source.disconnect();
      audioCtx.close();

      // Merge Float32 chunks → 16-bit WAV
      const totalLen = chunks.reduce((n, c) => n + c.length, 0);
      const merged   = new Float32Array(totalLen);
      let off = 0;
      for (const c of chunks) { merged.set(c, off); off += c.length; }
      const wavBlob = encodeWAV(merged, sampleRate);

      setStatus('uploading');
      const form = new FormData();
      form.append('file', wavBlob, 'pi-ring-recording.wav');
      form.append('user_id', String(userIdRef.current));
      form.append('auto_save', 'true');

      console.log('[Serial/Mic] POST', PREDICT_BASE + '/predict', '| user_id:', userIdRef.current);
      const res = await fetch(`${PREDICT_BASE}/predict`, { method: 'POST', body: form });
      
      if (res.status === 422) {
        const errorJson = await res.json();
        if (errorJson.is_invalid_audio) {
          setStatus('invalid_audio');
          if (onErrorRef.current) onErrorRef.current(errorJson.message);
          setTimeout(() => { if (statusRef.current === 'invalid_audio') setStatus('listening'); }, 5000);
          return;
        }
      }

      if (!res.ok) throw new Error('Predict HTTP ' + res.status + ': ' + res.statusText);

      const result = await res.json();
      console.log('[Serial/Mic] Predict result:', JSON.stringify(result));

      autoSavedRef.current = !!result.auto_saved;
      setStatus('done');
      if (onAudioReceivedRef.current) onAudioReceivedRef.current(result);
      setTimeout(() => { if (statusRef.current === 'done') setStatus('listening'); }, 2000);

    } catch (err) {
      console.error('[Serial/Mic] Record error:', err);
      setStatus('error');
      if (onErrorRef.current) onErrorRef.current(err.message);
      setTimeout(() => { if (statusRef.current === 'error') setStatus('listening'); }, 2000);
    } finally {
      micRecordingRef.current = false;
    }
  }

  // ---- Main serial effect — runs ONCE per serialPort ----
  useEffect(() => {
    if (!serialPort) return;

    runningRef.current = true;

    // Manual button uses laptop mic instead of sending START to the ring
    manualStartRef.current = recordFromMic;

    // -- Build 44-byte WAV header + PCM audio blob --
    const buildWAV = () => {
      const totalLen = audioBufferRef.current.reduce((n, c) => n + c.length, 0);
      const audio    = new Uint8Array(totalLen);
      let off = 0;
      for (const chunk of audioBufferRef.current) { audio.set(chunk, off); off += chunk.length; }

      const hdr  = new Uint8Array(44);
      const view = new DataView(hdr.buffer);
      const byteRate   = SAMPLE_RATE * CHANNELS * (BITS_PER_SAMPLE / 8);
      const blockAlign = CHANNELS * (BITS_PER_SAMPLE / 8);
      view.setUint32(0,  0x52494646, false); // RIFF
      view.setUint32(4,  36 + audio.length, true);
      view.setUint32(8,  0x57415645, false); // WAVE
      view.setUint32(12, 0x666d7420, false); // fmt
      view.setUint32(16, 16, true);
      view.setUint16(20, 1, true);           // PCM
      view.setUint16(22, CHANNELS, true);
      view.setUint32(24, SAMPLE_RATE, true);
      view.setUint32(28, byteRate, true);
      view.setUint16(32, blockAlign, true);
      view.setUint16(34, BITS_PER_SAMPLE, true);
      view.setUint32(36, 0x64617461, false); // data
      view.setUint32(40, audio.length, true);

      const wav = new Uint8Array(44 + audio.length);
      wav.set(hdr, 0); wav.set(audio, 44);
      return new Blob([wav], { type: 'audio/wav' });
    };

    // -- Upload WAV → /predict with auto_save=true --
    const uploadAudio = async () => {
      if (uploadingRef.current) return;

      // Clear fallback stop timer if it fired early
      if (stopTimerRef.current) { clearTimeout(stopTimerRef.current); stopTimerRef.current = null; }

      uploadingRef.current = true;
      streamingRef.current = false;

      const totalBytes = totalBytesRef.current;
      console.log('[Serial] Upload: total bytes collected =', totalBytes);

      try {
        if (totalBytes < 1000) {
          console.warn('[Serial] Too few bytes, skipping upload');
          setStatus('listening');
          return;
        }

        setStatus('uploading');
        const wavBlob = buildWAV();
        console.log('[Serial] WAV blob size:', wavBlob.size);
        console.log('[Serial] POST', PREDICT_BASE + '/predict', '| user_id:', userIdRef.current);

        const form = new FormData();
        form.append('file', wavBlob, 'pi-ring-recording.wav');
        form.append('user_id', String(userIdRef.current));
        form.append('auto_save', 'true');

        const res = await fetch(`${PREDICT_BASE}/predict`, { method: 'POST', body: form });

        if (res.status === 422) {
          const errorJson = await res.json();
          if (errorJson.is_invalid_audio) {
            setStatus('invalid_audio');
            if (onErrorRef.current) onErrorRef.current(errorJson.message);
            setTimeout(() => { if (statusRef.current === 'invalid_audio') setStatus('listening'); }, 5000);
            return;
          }
        }

        if (!res.ok) throw new Error('Predict HTTP ' + res.status + ': ' + res.statusText);

        const result = await res.json();
        console.log('[Serial] Predict result:', JSON.stringify(result));

        if (result.auto_saved) {
          console.log('[Serial] Auto-save SUCCESS, report_id:', result.report_id);
          autoSavedRef.current = true;
        } else {
          console.warn('[Serial] Auto-save FAILED:', result.save_error || 'unknown');
          autoSavedRef.current = false;
        }

        setStatus('done');
        if (onAudioReceivedRef.current) onAudioReceivedRef.current(result);

        // Go back to listening quickly — widget stays visible the whole time
        setTimeout(() => { setStatus('listening'); }, 2000);

      } catch (err) {
        console.error('[Serial] Upload error:', err);
        setStatus('error');
        if (onErrorRef.current) onErrorRef.current(err.message);
        setTimeout(() => { setStatus('listening'); }, 2000);
      } finally {
        audioBufferRef.current = [];
        totalBytesRef.current  = 0;
        uploadingRef.current   = false;
      }
    };

    // -- Handle bytes from ESP32 --
    // Two modes:
    //   TEXT MODE  (streamingRef=false): parse ASCII lines, watch for [STREAMING]
    //   BINARY MODE (streamingRef=true): accumulate raw PCM, watch for [STOPPED]
    //
    // The tricky case: [STREAMING]\n arrives in the SAME chunk as first audio bytes.
    // We handle this by scanning raw bytes for the newline after [STREAMING], then
    // switching the remainder of that chunk to binary mode immediately.
    const handleData = (chunk) => {
      if (!streamingRef.current) {
        // --- TEXT MODE ---
        // Scan for "[STREAMING]\n" in raw bytes first (avoids TextDecoder corrupting boundary)
        const streamingSeq = new TextEncoder().encode('[STREAMING]\n');
        const streamingSeqR = new TextEncoder().encode('[STREAMING]\r\n');

        let switchIdx = findMarker(chunk, streamingSeq);
        let seqLen = streamingSeq.length;
        if (switchIdx === -1) {
          switchIdx = findMarker(chunk, streamingSeqR);
          seqLen = streamingSeqR.length;
        }

        if (switchIdx !== -1) {
          // Decode and log the text part before [STREAMING]
          const textPart = chunk.slice(0, switchIdx);
          if (textPart.length > 0) {
            const txt = new TextDecoder().decode(textPart);
            for (const line of txt.split('\n')) {
              const t = line.trim();
              if (t) console.log('[Serial] <<< ESP32 (text):', t);
            }
          }

          console.log('[Serial] <<< [STREAMING] detected — switching to binary mode');
          streamingRef.current = true;
          audioBufferRef.current = [];
          totalBytesRef.current  = 0;
          textBufRef.current     = '';
          autoSavedRef.current   = false;
          setStatus('recording');

          // Set 10-second fallback upload timer in case [STOPPED] is missed
          if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
          stopTimerRef.current = setTimeout(() => {
            console.log('[Serial] Fallback stop timer fired');
            if (streamingRef.current) uploadAudio();
          }, (RECORD_SECONDS + 2) * 1000);

          // Remaining bytes after [STREAMING]\n → binary mode
          const remainder = chunk.slice(switchIdx + seqLen);
          if (remainder.length > 0) handleData(remainder); // recursive, now in binary mode
          return;
        }

        // No [STREAMING] found — decode as text and log
        const text = new TextDecoder().decode(chunk);
        textBufRef.current += text;
        const lines = textBufRef.current.split('\n');
        textBufRef.current = lines.pop() || '';

        for (const line of lines) {
          const t = line.trim();
          if (!t) continue;
          console.log('[Serial] <<< ESP32:', t);
          if (t === '[READY]')   setStatus('listening');
          if (t === '[BUTTON]')  console.log('[Serial] Ring button pressed');
          if (t === '[STOPPED]') console.log('[Serial] Received STOPPED while in text mode');
        }

      } else {
        // --- BINARY MODE ---
        // Check if this chunk contains [STOPPED] marker
        const stopIdx = findMarker(chunk, STOPPED_MARKER);

        if (stopIdx !== -1) {
          // Save audio bytes before the marker
          if (stopIdx > 0) {
            const audioPart = chunk.slice(0, stopIdx);
            audioBufferRef.current.push(audioPart);
            totalBytesRef.current += audioPart.length;
          }
          console.log('[Serial] <<< [STOPPED] — total bytes:', totalBytesRef.current);
          streamingRef.current = false;
          uploadAudio();
          return;
        }

        // Pure audio data
        if (!uploadingRef.current) {
          audioBufferRef.current.push(chunk);
          totalBytesRef.current += chunk.length;
        }
      }
    };

    // -- Main read loop --
    const run = async () => {
      try {
        if (!serialPort.readable) {
          console.log('[Serial] Opening port at', BAUD_RATE, 'baud...');
          await serialPort.open({ baudRate: BAUD_RATE });
        }

        if (!serialPort.writable) {
          console.error('[Serial] Port not writable');
          setStatus('error');
          return;
        }

        writerRef.current = serialPort.writable.getWriter();
        console.log('[Serial] Connected. Listening for ring button press...');
        setStatus('listening');

        while (runningRef.current && serialPort.readable) {
          const reader = serialPort.readable.getReader();
          readerRef.current = reader;
          try {
            while (runningRef.current) {
              const { value, done } = await reader.read();
              if (done) break;
              if (value && value.length > 0) {
                // Use the Uint8Array directly (NOT value.buffer which may be oversized)
                handleData(value instanceof Uint8Array ? value : new Uint8Array(value));
              }
            }
          } finally {
            reader.releaseLock();
            readerRef.current = null;
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError' && runningRef.current) {
          console.error('[Serial] Fatal:', err);
          setStatus('error');
          if (onErrorRef.current) onErrorRef.current(err.message);
        }
      }
    };

    run();

    return () => {
      runningRef.current = false;
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
      if (writerRef.current)  { try { writerRef.current.releaseLock(); } catch (_) {} writerRef.current = null; }
      if (readerRef.current)  { try { readerRef.current.cancel(); }     catch (_) {} readerRef.current = null; }
      manualStartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialPort]);

  const { status, isRecording, progress, autoSaved } = uiState;

  return (
    <>
      <div style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        background: 'rgba(0,0,0,0.88)',
        color: 'white',
        padding: '0.75rem 1.25rem',
        borderRadius: 12,
        zIndex: 9998,
        minWidth: 240,
        fontSize: '0.9rem',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
      }}>

        {/* Status row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
            background:
              status === 'recording' ? '#f44336' :
              status === 'uploading' ? '#ff9800' :
              status === 'done'      ? '#4caf50' :
              status === 'invalid_audio' ? '#ff9800' :
              status === 'error'     ? '#f44336' : '#4caf50',
            animation:
              status === 'recording' ? 'sah-pulse 1s infinite' :
              status === 'listening' ? 'sah-breathe 2.5s infinite' : 'none'
          }} />
          <span style={{ fontWeight: 600 }}>
            {status === 'idle'      && 'Pi Ring — Connecting...'}
            {status === 'listening' && 'Pi Ring — Press button to record'}
            {status === 'recording' && 'Pi Ring — Recording...'}
            {status === 'uploading' && 'Pi Ring — Analyzing...'}
            {status === 'done'      && (autoSaved ? 'Pi Ring — Saved to Dashboard! ✓' : 'Pi Ring — Analysis done (save failed)')}
            {status === 'invalid_audio' && 'Human speech not detected. Please speak clearly into the microphone.'}
            {status === 'error'     && 'Pi Ring — Error, try again'}
          </span>
        </div>

        {/* Progress bar (recording) */}
        {isRecording && (
          <>
            <div style={{ width: '100%', height: 6, background: 'rgba(255,255,255,0.2)', borderRadius: 3, overflow: 'hidden', marginBottom: 4 }}>
              <div style={{ width: `${progress}%`, height: '100%', background: '#4caf50', transition: 'width 0.2s ease' }} />
            </div>
            <div style={{ fontSize: '0.82rem', opacity: 0.8 }}>
              {progress}% — {RECORD_SECONDS}s recording
            </div>
          </>
        )}

        {/* Uploading message */}
        {status === 'uploading' && (
          <div style={{ fontSize: '0.82rem', opacity: 0.8 }}>
            Predicting emotions &amp; saving to dashboard...
          </div>
        )}

        {/* Done message */}
        {status === 'done' && (
          <div style={{ fontSize: '0.82rem', opacity: 0.8 }}>
            {autoSaved
              ? 'Dashboard updated. Press ring button again to record.'
              : 'Analysis complete but not saved — check console for details.'}
          </div>
        )}

        {/* Manual Record — always visible except when actively recording or uploading */}
        {!isRecording && status !== 'uploading' && (
          <button
            onClick={() => manualStartRef.current && manualStartRef.current()}
            disabled={status === 'idle'}
            style={{
              marginTop: 8, width: '100%',
              background: status === 'idle' ? '#555' : '#f44336',
              color: 'white', border: 'none', borderRadius: 6,
              padding: '6px 0', fontSize: '0.82rem',
              cursor: status === 'idle' ? 'not-allowed' : 'pointer',
              fontWeight: 600, opacity: status === 'idle' ? 0.6 : 1
            }}
          >
            Manual Record (8s)
          </button>
        )}
      </div>

      <style>{`
        @keyframes sah-pulse   { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes sah-breathe { 0%,100%{opacity:1} 50%{opacity:0.55} }
      `}</style>
    </>
  );
}

export default SerialAudioHandler;
